import {
  Attachment,
  BffOe,
  BffOeInitiatives,
  BffOes,
  BffOverviewPage,
  Currency,
  DashboardFilterData,
  Initiative,
  OeKpis,
  OverviewFilterData,
  Region,
  UnsavedAttachment,
} from "aint-types";
import { formatDate } from "aint-utils";

import { STRAPI_URL } from "../env";

export async function fetchOEs(
  payload?: DashboardFilterData,
  currency?: Currency
): Promise<BffOes> {
  const params = new URLSearchParams();

  try {
    if (payload) {
      params.append("filter", encodeURIComponent(JSON.stringify(payload)));
      params.append("currency", currency ?? "EUR");
    }
    const url = new URL(`${STRAPI_URL}/api/bff-oe`);

    url.search = params.toString();

    const response = await fetch(url.toString(), { credentials: "include" });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    return (await response.json()) as BffOes;
  } catch {
    return {} as BffOes;
  }
}

/**
 * I created another fetcher to fetch the overview, because of the different filter data.
 * This is something I will expand when I work on the overview page.
 * likely will have to create a different endpoint for the overview.
 */
export async function fetchOEsOverview(
  payload?: OverviewFilterData
): Promise<BffOes> {
  const params = new URLSearchParams();

  if (payload) {
    params.append("filter", encodeURIComponent(JSON.stringify(payload)));
  }

  try {
    const url = new URL(`${STRAPI_URL}/api/bff-oe`);

    url.search = params.toString();

    const response = await fetch(url.toString(), { credentials: "include" });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    return (await response.json()) as BffOes;
  } catch {
    return {} as BffOes;
  }
}

export async function fetchOEOverview(
  payload?: OverviewFilterData
): Promise<BffOverviewPage> {
  try {
    const params = new URLSearchParams();

    if (payload) {
      params.append("filter", encodeURIComponent(JSON.stringify(payload)));
    }
    const url = new URL(`${STRAPI_URL}/api/bff-oe-overview`);
    url.search = params.toString();

    const response = await fetch(url.toString(), { credentials: "include" });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    return (await response.json()) as BffOverviewPage;
  } catch {
    return {} as never;
  }
}

export async function fetchOE(id: string): Promise<BffOe | undefined> {
  try {
    const response = await fetch(`${STRAPI_URL}/api/bff-oe/${id}`, {
      credentials: "include",
    });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    const data = (await response.json()) as Promise<BffOe>;

    return data;
  } catch {
    return;
  }
}

export async function fetchOEInitiatives(
  oeId: string
): Promise<BffOeInitiatives> {
  try {
    const url = new URL(`${STRAPI_URL}/api/bff-oe-initiatives/${oeId}`);

    const response = await fetch(url.toString(), {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }
    const data: BffOeInitiatives = (await response.json()) as BffOeInitiatives;
    return data;
  } catch {
    return {} as BffOeInitiatives;
  }
}

export async function fetchEditInitiative(
  oeId: string,
  initiativeUid: string
): Promise<Initiative | undefined> {
  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-initiatives/${oeId}/initiative/${initiativeUid}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `fetchEditInitiative failed with status ${response.status}.`
      );
    }

    return (await response.json()) as Promise<Initiative>;
  } catch {
    return undefined;
  }
}

export async function saveInitiative({
  id,
  initiative,
  savedAttachments = [],
  unsavedAttachments = [],
  isReportingUpdate = false,
}: {
  id: number | string | undefined;
  initiative: Initiative;
  savedAttachments?: Attachment[];
  unsavedAttachments?: UnsavedAttachment[];
  isReportingUpdate?: boolean;
}): Promise<Initiative | undefined> {
  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-initiatives/${String(id)}`,
      {
        body: JSON.stringify({
          ...initiative,
          startDate: formatDate(initiative.startDate),
          endDate: formatDate(initiative.endDate),
          monitoringEndDate: formatDate(initiative.monitoringEndDate),
          isReportingUpdate,
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
      }
    );

    if (!response.ok) {
      throw new Error(`saveInitiative failed with status ${response.status}.`);
    }

    const savedInitiative = (await response.json()) as Initiative;

    // delete attachments that have been removed from savedAttachments
    const documentIdsToDelete: string[] = [];

    (savedInitiative.supportingDocuments ?? []).forEach((doc) => {
      if (
        savedAttachments.findIndex((savedDoc) => savedDoc.id === doc.id) === -1
      ) {
        documentIdsToDelete.push(doc.id);
      }
    });

    for (const docId of documentIdsToDelete) {
      await deleteSupportingDocument(docId, savedInitiative.id.toString());
    }

    // upload new documents.
    for (const fileToSave of unsavedAttachments) {
      await uploadSupportingDocument(
        fileToSave.file,
        fileToSave.comment ?? "",
        savedInitiative.id.toString()
      );
    }

    return savedInitiative;
  } catch {
    return undefined;
  }
}

export async function addNewInitiative(
  oeId: string,
  initiative: Initiative,
  fileEntries: UnsavedAttachment[]
): Promise<Initiative | undefined> {
  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-initiatives/${oeId}/initiative`,
      {
        body: JSON.stringify({
          ...initiative,
          startDate: formatDate(initiative.startDate),
          endDate: formatDate(initiative.endDate),
          monitoringEndDate: formatDate(initiative.monitoringEndDate),
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
      }
    );

    if (!response.ok) {
      throw new Error(`addInitiative failed with status ${response.status}.`);
    }

    const createdInitiative = (await response.json()) as Initiative;

    if (fileEntries.length > 0) {
      await Promise.all(
        fileEntries.map((entry) =>
          uploadSupportingDocument(
            entry.file,
            entry.comment ?? "",
            createdInitiative.id.toString()
          )
        )
      );
    }

    return createdInitiative;
  } catch (error) {
    console.log(error);
    return undefined;
  }
}

export async function markInitiativeDeleted(
  initiativeId: number
): Promise<void> {
  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-initiatives/${initiativeId}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );

    if (!response.ok) {
      throw new Error(
        `markInitiativeDeleted failed with status ${response.status}`
      );
    }
  } catch {
    return undefined;
  }
}

export async function getSupportingDocumentsForInitiative(
  initiativeId: string
): Promise<Attachment[]> {
  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-initiatives/${initiativeId}/supportingDocument`,
      {
        method: "GET",
        credentials: "include",
      }
    );
    if (!response.ok) {
      throw new Error(
        `Fetching Documents for Initiative failed with status ${response.status}.`
      );
    }

    return (await response.json()) as Attachment[];
  } catch (error) {
    console.log(error);
    return [];
  }
}

export async function uploadSupportingDocument(
  file: File,
  comment: string,
  initiativeId?: string
) {
  if (!file || !initiativeId) {
    return;
  }
  const formData = new FormData();

  formData.append("file", file, file.name);
  formData.append("comment", comment);

  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-initiatives/${initiativeId}/supportingDocument`,
      {
        method: "POST",
        credentials: "include",
        body: formData,
      }
    );
    if (response.status >= 400) {
      throw new Error(
        `Uploading Document failed with status ${response.status}.`
      );
    }
  } catch {
    return;
  }
}

export async function deleteSupportingDocument(
  docId: string,
  initiativeId: string
) {
  if (!docId || !initiativeId) {
    return;
  }

  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-initiatives/${initiativeId}/supportingDocument/${docId}`,
      {
        method: "DELETE",
        credentials: "include",
      }
    );
    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }
  } catch {
    return;
  }
}

export async function fetchAllRegions(
  payload?: DashboardFilterData | OverviewFilterData
) {
  const params = new URLSearchParams();

  try {
    if (payload) {
      params.append("filter", encodeURIComponent(JSON.stringify(payload)));
    }
    const url = new URL(`${STRAPI_URL}/api/bff-oe-region`);

    url.search = params.toString();

    const response = await fetch(url.toString(), { credentials: "include" });

    if (!response.ok) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    return (await response.json()) as Pick<Region, "id" | "regionName">[];
  } catch {
    return [];
  }
}

export async function saveOeKpis(
  oeId: string,
  kpis: OeKpis
): Promise<BffOe | undefined> {
  try {
    const response = await fetch(`${STRAPI_URL}/api/bff-oe-kpis/${oeId}`, {
      body: JSON.stringify(kpis),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
    });

    if (!response.ok) {
      throw new Error(`saveInitiative failed with status ${response.status}.`);
    }
    return response.json();
  } catch {
    return undefined;
  }
}

export async function uploadOeKpis(
  file?: File,
  oeId?: string
): Promise<BffOe | undefined> {
  if (!file || !oeId) {
    return;
  }
  const formData = new FormData();

  formData.append("file", file, file.name);

  try {
    const response = await fetch(
      `${STRAPI_URL}/api/bff-oe-kpis/${oeId}/import`,
      {
        method: "PUT",
        credentials: "include",
        body: formData,
      }
    );
    if (response.status >= 400) {
      throw new Error(
        `Uploading Document failed with status ${response.status}.`
      );
    }

    return response.json();
  } catch {
    return undefined;
  }
}

export async function uploadFXRates(params: {
  actualsFile?: File;
  plannedRatesFile?: File;
}): Promise<void> {
  const { actualsFile, plannedRatesFile } = params;
  if (!actualsFile && !plannedRatesFile) {
    return;
  }
  const formData = new FormData();
  if (plannedRatesFile) {
    formData.append(
      "plannedRatesFile",
      plannedRatesFile,
      plannedRatesFile.name
    );
  }
  if (actualsFile) {
    formData.append("actualsFile", actualsFile, actualsFile.name);
  }

  try {
    const response = await fetch(`${STRAPI_URL}/api/fx-rates-parser`, {
      method: "POST",
      credentials: "include",
      body: formData,
    });
    if (response.status >= 400) {
      throw new Error(
        `Uploading Document failed with status ${response.status}.`
      );
    }

    return response.json();
  } catch {
    return undefined;
  }
}

// TODO: Are these still needed?
// export async function fetchOEComments(
//   oeId: number
// ): Promise<CommentsByYearAndQuarter> {
//   try {
//     const response = await fetch(
//       `${STRAPI_URL}/api/bff-oe-comment/${String(oeId)}`,
//       {
//         credentials: "include",
//       }
//     );
//
//     if (!response.ok) {
//       throw new Error(
//         `Fetching comments for oe failed with status ${response.status}.`
//       );
//     }
//
//     return (await response.json()) as CommentsByYearAndQuarter;
//   } catch {
//     return {} as CommentsByYearAndQuarter;
//   }
// }

// export async function saveImpactPlansForOE(oe: BffOe) {
//   try {
//     const response = await fetch(
//       `${STRAPI_URL}/api/bff-oe-impact-plans/${oe.id}`,
//       {
//         method: "PUT",
//         credentials: "include",
//         body: JSON.stringify(oe.impactPlans),
//         headers: {
//           "Content-Type": "application/json",
//         },
//       }
//     );
//
//     if (!response.ok) {
//       throw new Error(
//         `saveImpactPlansForOE failed with status ${response.status}`
//       );
//     }
//
//     return (await response.json()) as BffOe;
//   } catch {
//     return undefined;
//   }
// }

// export async function saveCommentsForEO(
//   oeId: number,
//   comments: CommentsByYearAndQuarter
// ): Promise<undefined> {
//   try {
//     const response = await fetch(`${STRAPI_URL}/api/bff-oe-comment/${oeId}`, {
//       body: JSON.stringify(comments),
//       credentials: "include",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       method: "PUT",
//     });
//
//     if (!response.ok) {
//       throw new Error(
//         `saveCommentsForEO failed with status ${response.status}.`
//       );
//     }
//   } catch {
//     return undefined;
//   }
// }
